import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { useIsMobileWithBreakpoint } from 'hooks/useIsMobileWithBreakpoint';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import PlayButton from './components/PlayButton';
import VolumeControlBar from './components/VolumeControl';
import TimeValues from './components/TimeValues';
import ProgressBar from './components/ProgressBar';

import styles from './styles.module.scss';

const AudioPlayer = ({ trackSrc, autoPlay, className, theme }) => {
	const [percentageProgress, setPercentageProgress] = useState(0);
	const audioPlayerRef = useRef(null);
	const progressBarRef = useRef(null);
	const volumeBarRef = useRef(null);
	const isMobile = useIsMobileWithBreakpoint('mobileLarge');

	const {
		isPlaying,
		currentTime,
		duration,
		calculateTime,
		playPauseToggle,
		changeRange,
		changeVolume,
		dropRange
	} = useAudioPlayer({
		audioPlayerRef,
		progressBarRef,
		volumeBarRef,
		trackSrc
	});

	useEffect(() => {
		if (autoPlay) {
			dropRange();
		}
	}, [trackSrc]);

	useEffect(() => {
		if (theme === 'simpleButton' && currentTime) {
			setPercentageProgress((100 * currentTime) / duration || 0);

			if (parseInt(currentTime, 10) >= parseInt(duration, 10)) {
				setPercentageProgress(0);
				playPauseToggle();
			}
		}
	}, [currentTime]);

	return (
		<div className={cx(styles.AudioPlayer, className)}>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
			<audio
				ref={audioPlayerRef}
				src={trackSrc || ''}
				autoPlay={autoPlay}
				preload="metadata"
			/>
			<PlayButton
				play={playPauseToggle}
				isPlaying={isPlaying}
				theme={theme}
				percentageProgress={percentageProgress}
			/>
			{theme !== 'simpleButton' && (
				<>
					<VolumeControlBar
						{...{
							volumeBarRef,
							changeVolume,
							isMobile,
							theme
						}}
					/>
					<TimeValues
						key={duration}
						currentTime={currentTime}
						duration={duration}
						calculateFunction={calculateTime}
					>
						<ProgressBar
							reference={progressBarRef}
							onChangeCallback={changeRange}
							theme={theme}
						/>
					</TimeValues>
				</>
			)}
		</div>
	);
};

AudioPlayer.initialProps = {
	autoPlay: false
};

AudioPlayer.propTypes = {
	trackSrc: PropTypes.string.isRequired,
	autoPlay: PropTypes.bool,
	className: PropTypes.string,
	theme: PropTypes.oneOf(['light', 'simpleButton'])
};

export default AudioPlayer;
